import {
    GlobalEventCallback,
    Method,
    VisitOptions,
} from '@inertiajs/core/types/types';
import { router } from '@inertiajs/react';
import * as React from 'react';
import {
    FormEvent,
    RefObject,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import formDataToFlatObject from '../../helpers/forms/formDataToFlatObject';
import Indexable from '../../types/DataStructure/Indexable';
import ValidationErrors from '../../types/UI/ValidationErrors';

import { FormContext } from './FormContext';

export type FormHandles = {
    submit: (extraData?: Indexable) => void;
};

type ErrorResponse = { errors: ValidationErrors };

type FormProps<TSuccess> = {
    onSubmit?: (data: FormData) => void;
    onSuccess?: (response: TSuccess) => void;
    onFinish?: (response: TSuccess | ErrorResponse) => void;
    modifyFormData?: (data: FormData) => FormData;
    onBeforeSubmit?: (data: FormData) => PromiseLike<any> | false;
    resetOnSuccess?: boolean;
    inertiaOptions?: VisitOptions;
    /** Sometimes you don't want the submit button to be re-enabled after a successful submission. */
    keepInSubmittingStateAfterSuccess?: boolean;
    formRef?: RefObject<FormHandles>;
} & Omit<React.HTMLProps<HTMLFormElement>, 'onError' | 'onSubmit'>;

const Form = <TSuccess,>({
    onSubmit,
    onSuccess,
    onFinish,
    modifyFormData,
    onBeforeSubmit,
    inertiaOptions = {},
    resetOnSuccess = false,
    keepInSubmittingStateAfterSuccess = false,
    disabled = false,
    children,
    formRef,
    ...formProps
}: FormProps<TSuccess>) => {
    const [submitting, setSubmitting] = useState(false);
    const [hasErrors, setHasErrors] = useState(false);
    const [formKey, setFormKey] = useState(1);
    const internalRef = useRef<HTMLFormElement>(null);

    const handleSuccess: GlobalEventCallback<'success'> = (page) => {
        if (!internalRef.current) {
            return;
        }

        setHasErrors(false);

        if (resetOnSuccess) {
            setFormKey(formKey + 1);
        }

        if (!keepInSubmittingStateAfterSuccess) {
            setSubmitting(false);
        }

        onSuccess && onSuccess(page.props as TSuccess);
    };

    const handleError: GlobalEventCallback<'error'> = () => {
        if (!internalRef.current) {
            return;
        }

        setSubmitting(false);
    };

    const handleCancel: GlobalEventCallback<'cancel'> = () => {
        if (!internalRef.current) {
            return;
        }

        setSubmitting(false);
    };

    const handleFinish: GlobalEventCallback<'finish'> = (event) => {
        if (!internalRef.current) {
            return;
        }

        onFinish && onFinish(event.data as TSuccess | ErrorResponse);
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        await submit();
    };

    const submit = async (extraData?: Indexable) => {
        if (disabled || submitting || !internalRef.current) {
            return;
        }

        const form = internalRef.current;
        let formData = new FormData(form);

        /**
         * When using the imperative handle, extra data can be added to the
         * request programmatically.
         */
        if (extraData) {
            Object.keys(extraData).forEach((key) =>
                formData.append(key, extraData[key])
            );
        }

        formData = modifyFormData ? modifyFormData(formData) : formData;

        if (onBeforeSubmit) {
            const proceedSubmitting = await onBeforeSubmit(formData);
            if (proceedSubmitting === false) {
                return;
            }
        }

        if (onSubmit) {
            onSubmit(formData);
        }

        setSubmitting(true);

        const data =
            form.method === 'get' ? formDataToFlatObject(formData) : formData;

        router.visit(form.action, {
            method: form.method as Method,
            data: data,
            onSuccess: handleSuccess,
            onError: handleError,
            onCancel: handleCancel,
            onFinish: handleFinish,

            preserveState: true,
            ...inertiaOptions,
        });
    };

    useImperativeHandle(formRef, () => ({ submit: submit }), [
        onBeforeSubmit,
        onSubmit,
    ]);

    return (
        <form
            {...formProps}
            onSubmit={handleSubmit}
            ref={internalRef}
            key={formKey}
        >
            <FormContext.Provider
                value={{ submitting, hasErrors, submit, disabled }}
            >
                {children}
            </FormContext.Provider>
        </form>
    );
};

export default Form;
